import { AttributeDefinitionDc, ClassifyParams } from "@evergis/api";

import { SYMBOL_SIZE_PARAMETERS } from "../evergis/core/legend/legendDefaults";
import { ClassificationState } from "../ducks/classification/classification";
import { ParameterValueWithSymbol } from "./style";

export type NestedClassificationParameters =
  | "fill.color"
  | "fill.hatchColor"
  | "stroke.width"
  | "stroke.color"
  | "textBackground.fill.color"
  | "background.fillColor"
  | "background.strokeColor"
  | "background.strokeWidth"
  | "figure.strokeColor"
  | "figure.strokeWidth"
  | "figure.fillColor";
export type SimpleClassificationParameters =
  | "fillColor"
  | "strokeColor"
  | "strokeWidth"
  | "fontColor"
  | "haloColor"
  | "size"
  | "symbol"
  | "angle"
  | "maskedColor"
  | "endingSize"
  | "beginningSize"
  | "maxObjectCount"
  | "gridSize"
  | "selectionObjectsLimit"
  | "offset";
export type ClassificationParameter = NestedClassificationParameters | SimpleClassificationParameters;
export type ClassificationParameterExtended = ClassificationParameter | "width" | "height";

export type ClassifyType = ClassifyParams["type"];

export type ClassificationAttribute = AttributeDefinitionDc & { name: string };

export interface ClassBase {
  title: string | null;
  parameterValue: ParameterValueWithSymbol;
  attribute?: ClassificationAttribute;
}

export type UniqueClass = ClassBase & {
  uniqueValue: string;
};

export type RangeClass = ClassBase & {
  from: number | string | null;
  to: number | string | null;
};

export type ClassificationClass = UniqueClass | RangeClass;
export type ClassificationValues = ClassificationClass[];
export type ClassificationSegmentValues = Record<number, ClassificationState>;

export const enum ClassificationType {
  Range,
  Unique,
}

export const enum ClassificationView {
  Size,
  Color,
  Symbol,
}

export interface Classification<
  Type extends ClassificationType = ClassificationType,
  Value extends ClassificationClass = ClassificationClass,
> {
  parameter?: ClassificationParameterExtended;
  attribute?: string;
  type: Type;
  values: Value[];
}

export type RangeClassification = Classification<ClassificationType.Range, RangeClass>;

export type UniqueClassification = Classification<ClassificationType.Unique, UniqueClass>;

export function isRangeClass(value: ClassificationClass): value is RangeClass {
  if (value && "from" in value) {
    return true;
  }
}
export function isRangeClasses(values: ClassificationValues): values is RangeClass[] {
  return Array.isArray(values) && values.length > 0 ? isRangeClass(values[0]) : true;
}

export function isUniqueClass(value: ClassificationClass): value is UniqueClass {
  if (value && "uniqueValue" in value) {
    return true;
  }
}

export function isUniqueClasses(values: ClassificationValues): values is UniqueClass[] {
  return Array.isArray(values) && values.length > 0 ? isUniqueClass(values[0]) : true;
}

export const isExtendedParameter = (parameter: string): parameter is ClassificationParameterExtended =>
  SYMBOL_SIZE_PARAMETERS.includes(parameter);

export const isSimpleParameter = (parameter: string): parameter is ClassificationParameter =>
  !isExtendedParameter(parameter);
